import { useEffect, useRef, useState } from 'react';
import { useLimits } from '../state/store';
import { daysUntil, shortDate } from '../lib/format';
import type { FullPosition } from '../state/derive';
import type { EarningsDate, Watch } from '../types';

export type EarningsSort = 'soonest' | 'biggest';

/* Earnings ahead — §7.1.
 *
 * A carousel rather than a row that overflows: on a phone one card sits in the
 * middle, larger than its neighbours, and the strip snaps to it. A row that
 * simply ran off the edge left a different, arbitrary number of cards showing on
 * every screen, which read as an accident rather than a decision.
 *
 * Watched companies appear in the same strip. The date a company reports is the
 * same fact whether it is in the book or not. What differs is what is at stake:
 * a watched name is tagged and never asks for a plan, because there is no money
 * behind it to have a plan about. */
export function EarningsAhead({
  positions, watchlist = [], earnings, sort = 'soonest', onOpen, onOpenWatch,
}: {
  positions: FullPosition[];
  watchlist?: Watch[];
  earnings: EarningsDate[];
  sort?: EarningsSort;
  onOpen: (ticker: string) => void;
  onOpenWatch?: (symbol: string) => void;
}) {
  const limits = useLimits();
  const bySymbol = new Map(earnings.map((e) => [e.symbol, e]));

  interface Row {
    key: string;
    label: string;
    date: EarningsDate;
    away: number;
    weight: number;
    needsPlan: boolean;
    watched: boolean;
    open: () => void;
  }

  const rows: Row[] = [];

  for (const p of positions) {
    const e = bySymbol.get(p.yahooSymbol);
    if (!e?.date) continue;
    const away = daysUntil(e.date);
    if (away < 0) continue;
    rows.push({
      key: `p-${p.ticker}`,
      label: p.ticker,
      date: e,
      away,
      weight: p.weight,
      needsPlan: p.weight > limits.planBeforeEarningsOver && !p.thesis?.earningsPlan?.trim(),
      watched: false,
      open: () => onOpen(p.ticker),
    });
  }

  for (const w of watchlist) {
    const e = bySymbol.get(w.yahooSymbol);
    if (!e?.date) continue;
    const away = daysUntil(e.date);
    if (away < 0) continue;
    rows.push({
      key: `w-${w.id}`,
      label: w.ticker,
      date: e,
      away,
      // Nothing is riding on a watched name, so by size it belongs at the back.
      weight: 0,
      needsPlan: false,
      watched: true,
      open: () => onOpenWatch?.(w.yahooSymbol),
    });
  }

  rows.sort((a, b) => (sort === 'biggest' ? b.weight - a.weight : 0) || a.away - b.away);

  /* Which card is lit, in two phases, because they answer different questions.
   *
   * Untouched, the strip rests against the gutter and the card being read is
   * the first one — the middle of an unscrolled strip points at nobody in
   * particular. Once it has moved, the card in the middle is the one being
   * looked at. An earlier attempt tried to serve both with one geometric rule
   * and got the resting state wrong, which is the state seen most often.
   *
   * There is no CSS for "the one nearest the centre", so the strip is measured
   * on scroll, throttled to a frame, and only on the phone — where the carousel
   * is centred at all. */
  const strip = useRef<HTMLDivElement | null>(null);
  const [centred, setCentred] = useState<string | null>(null);

  useEffect(() => {
    const el = strip.current;
    if (!el || typeof window.matchMedia !== 'function') return;
    const phone = window.matchMedia('(max-width: 767px)');
    if (!phone.matches) { setCentred(null); return; }

    let frame = 0;
    const measure = () => {
      frame = 0;
      const kids = Array.from(el.children) as HTMLElement[];
      if (kids.length === 0) return setCentred(null);

      // Snapping can leave a few pixels behind, so "not yet scrolled" is a small
      // window rather than exactly zero — still a fraction of one card's width.
      if (el.scrollLeft < 24) return setCentred(kids[0]!.dataset['key'] ?? null);

      const box = el.getBoundingClientRect();
      const middle = box.left + box.width / 2;
      let best = kids[0]!;
      let shortest = Infinity;
      for (const child of kids) {
        const c = child.getBoundingClientRect();
        const distance = Math.abs(c.left + c.width / 2 - middle);
        if (distance < shortest) { shortest = distance; best = child; }
      }
      setCentred(best.dataset['key'] ?? null);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(measure); };

    measure();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [rows.length, sort]);

  if (rows.length === 0) {
    return <div className="empty">No earnings dates have come back for these tickers yet.</div>;
  }

  return (
    <div className="hscroll hscroll-even hscroll-centre" ref={strip}>
      {rows.map((r) => (
        <button
          key={r.key}
          data-key={r.key}
          data-centred={centred === r.key ? 'true' : undefined}
          className="earn-card"
          onClick={r.open}
        >
          <div className="earn-days num">
            {r.away === 0 ? 'Today' : r.away}
            {r.away === 0 ? null : <small>{r.away === 1 ? 'day' : 'days'}</small>}
          </div>
          <div className="earn-ticker">{r.label}</div>
          <div className="earn-date num">{shortDate(r.date.date!)}</div>
          <div className={r.date.estimated ? 'earn-est' : 'earn-date'}>
            {r.date.estimated ? 'Estimated' : 'Confirmed'}
          </div>
          {r.watched ? <div className="earn-date">Watching</div> : null}
          {r.needsPlan ? <div className="earn-plan">WRITE YOUR PLAN FIRST</div> : null}
        </button>
      ))}
    </div>
  );
}
